export type MissionCategory = 'Intro' | 'Chat' | 'Mission' | 'Game';

export enum MissionCategories {
  All = 'All',
  Intro = 'Intro',
  Game = 'Game',
  Mission = 'Mission',
  Chat = 'Chat',
}

export type MicStatus = 'idle' | 'listening' | 'stopping';

export type MissionLevel = 'NONE' | 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';

export interface Mission {
  locationSlug: string;
  displayName: string;
  category: MissionCategory;
  imageUrl: string;
  useInProd: boolean;
  topic?: string;
  level?: MissionLevel;
}

export interface MissionWithPrompt extends Mission {
  prompt: MissionPrompt;
}

export interface LanguageInfo {
  sourceLanguageLocale: string;
  sourceLanguageName: string;
  sourceLanguageId?: string;
  targetLanguageLocale: string;
  targetLanguageName: string;
  targetLanguageId?: string;
}

export interface MissionsResponse extends Record<string, unknown> {
  missions: Mission[];
}

export interface DialogueLine {
  isBot: boolean;
  isPrompt: boolean;
  textEn: string | null;
  textSrc: string | null;
  textTgt: string | null;
  textTgtRoman: string | null;
  translating?: boolean;
  translation?: boolean;
  fixedMessage?: string | null;
  diffForDisplay?: string[] | null;
}

export interface MissionPrompt {
  instructionsHumanSrc: string;
  nameBot: string;
  maxResponses: number;
}

export interface MissionResponse extends Record<string, unknown> {
  dialogue: DialogueLine[];
  prompt: MissionPrompt;
  languageInfo: LanguageInfo;
  missionInfo: Mission;
}

export interface DialogueResponse extends Record<string, unknown> {
  dialogue: DialogueLine[];
}

export interface GoalReachedResponse extends Record<string, unknown> {
  goalReached: boolean;
  messageLimitReached: boolean;
}

export interface FixMessageRequest extends Record<string, unknown> {
  message: string;
  conversationSlug: string;
  sessionId: string;
}

export interface FixMessageResponse extends Record<string, unknown> {
  fixedText: string;
  diffForDisplay: string[];
  inputInTargetLanguage?: boolean;
}

export interface HintResponse extends Record<string, unknown> {
  hints: DialogueLine[];
}

export type HintAnimationStatus = 'unset' | 'inactive' | 'scheduling' | 'scheduled';

export interface TranslateRequest extends Record<string, unknown> {
  sourceLanguageLocale: string;
  targetLanguageLocale: string;
  conversationSlug: string;
  text: string;
  sessionId: string;
}

export interface TranslateResponse extends Record<string, unknown> {
  languageInfo: LanguageInfo;
  translatedText: string;
  translatedTextRoman?: string;
}

export type MessageError = {
  text: string | undefined;
  type: MessageErrorType;
};

export enum MessageErrorType {
  ltMinLength = 'ltMinLength',
  gtMaxLength = 'gtMaxLength',
}

export interface SpeechToTextTokenResponse extends Record<string, unknown> {
  token: string;
  region: string;
  languages: string[];
}

export interface DialogueRequest {
  dialogue: DialogueLine[];
  lastMessageUsedHint?: boolean;
  sessionId: string;
}
